import {
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Flex,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Pagination,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Search, Plus, ArrowRight } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/shared/PageHeader';
import { useResearchConfigurationsLiteQuery } from '../../hooks/queries/useResearchConfigurationsLiteQuery';
import { useAuthUser } from '../../providers/AuthProvider';
import { buildSpectralAnalysisSnapshot } from '../../features/research/services/analysisBuilder';
import { spectralAnalysisStore } from '../../features/research/store/spectralAnalysisStore';
import type {
  SpectralAnalysisListItem,
  CreateSpectralAnalysisInput,
} from '../../features/research/types';
import { getConfiguration } from '../../services/configurationManager';

const formatDate = (value?: string | Date | null) => {
  if (!value) return 'n/a';
  return new Date(value).toLocaleString('it-IT');
};

const hasBaselineValue = (baseline: unknown) =>
  (typeof baseline === 'number' && Number.isFinite(baseline) && baseline > 0) ||
  (typeof baseline === 'string' &&
    baseline.trim().length > 0 &&
    baseline.trim() !== '0');

export function SpectralNormalizationPage() {
  const navigate = useNavigate();
  const user = useAuthUser();
  const [opened, { open, close }] = useDisclosure(false);
  const [analyses, setAnalyses] = useState<SpectralAnalysisListItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [searchId, setSearchId] = useState('');
  const [searchPatient, setSearchPatient] = useState('');
  const [searchDate, setSearchDate] = useState('');
  const [analysisPage, setAnalysisPage] = useState(1);
  const [page, setPage] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [debouncedSearchId] = useDebouncedValue(searchId, 250);
  const [debouncedSearchPatient] = useDebouncedValue(searchPatient, 250);
  const [debouncedSearchDate] = useDebouncedValue(searchDate, 250);

  const userId = user?.id?.toString();
  const pageSize = 25;
  const liteParams = useMemo(
    () => ({
      status: 'completed' as const,
      page,
      size: pageSize,
      searchId: debouncedSearchId,
      searchPatient: debouncedSearchPatient,
      searchDate: debouncedSearchDate,
    }),
    [debouncedSearchDate, debouncedSearchId, debouncedSearchPatient, page]
  );
  const {
    data: configurationsLite,
    isLoading,
    isFetching,
  } = useResearchConfigurationsLiteQuery(userId, liteParams);
  const filteredSessions = configurationsLite?.items ?? [];
  const orderedAnalyses = useMemo(
    () =>
      [...analyses].sort(
        (left, right) =>
          new Date(right.createdAt).getTime() -
          new Date(left.createdAt).getTime()
      ),
    [analyses]
  );
  const analysesPageSize = 10;
  const paginatedAnalyses = useMemo(() => {
    const start = (analysisPage - 1) * analysesPageSize;
    return orderedAnalyses.slice(start, start + analysesPageSize);
  }, [analysisPage, orderedAnalyses]);

  useEffect(() => {
    spectralAnalysisStore
      .listAnalyses()
      .then(setAnalyses)
      .catch(error => {
        notifications.show({
          color: 'red',
          title: 'Errore caricamento analisi',
          message:
            error instanceof Error
              ? error.message
              : 'Impossibile recuperare le analisi salvate.',
        });
      });
  }, []);

  const resetModal = () => {
    setName('');
    setDescription('');
    setSelectedIds([]);
    setSearchId('');
    setSearchPatient('');
    setSearchDate('');
    setPage(1);
  };

  const handleClose = () => {
    resetModal();
    close();
  };

  const toggleSession = (sessionId: number) => {
    setSelectedIds(current =>
      current.includes(sessionId)
        ? current.filter(id => id !== sessionId)
        : [...current, sessionId]
    );
  };

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      notifications.show({
        color: 'red',
        title: 'Nome richiesto',
        message: 'Inserisci un nome per l’analisi.',
      });
      return;
    }

    if (selectedIds.length < 3) {
      notifications.show({
        color: 'red',
        title: 'Sessioni mancanti',
        message: 'Seleziona almeno 3 sessioni completed.',
      });
      return;
    }

    setIsCreating(true);

    try {
      const input: CreateSpectralAnalysisInput = {
        name: trimmedName,
        description: description.trim() || undefined,
        sessionIds: selectedIds,
      };

      const fullSessions = await Promise.all(
        selectedIds.map(async sessionId => {
          const response = await getConfiguration(sessionId);
          return response.data;
        })
      );

      const snapshot = buildSpectralAnalysisSnapshot(
        input,
        fullSessions,
        user?.email || user?.name || 'Utente'
      );
      const persistedSnapshot =
        await spectralAnalysisStore.createAnalysis(snapshot);
      const nextAnalyses = await spectralAnalysisStore.listAnalyses();
      setAnalyses(nextAnalyses);
      setAnalysisPage(1);
      handleClose();
      navigate(
        `/research/spectral-normalization/legacy/${persistedSnapshot.analysisId}`
      );
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Errore creazione analisi',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile creare l’analisi spettrale.',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <PageHeader
        title='NORMALIZZAZIONE SPETTRALE'
        subtitle='Pipeline locale per validazione, cleaning, segmentazione e analisi FFT/PSD.'
        breadcrumbs={[
          { label: 'Ricerca', href: '/research' },
          { label: 'Normalizzazione spettrale' },
        ]}
        backUrl='/research'
        actions={
          <Group gap='xs'>
            <Button
              variant='light'
              onClick={() =>
                navigate('/research/spectral-normalization/documentation')
              }
            >
              Documentazione
            </Button>
            <Button leftSection={<Plus size={16} />} onClick={open}>
              Nuova analisi
            </Button>
          </Group>
        }
      />

      <Divider my='md' />

      <Stack gap='md'>
        <Card withBorder>
          <Group justify='space-between'>
            <div>
              <Title order={4}>Analisi salvate</Title>
              <Text c='dimmed' size='sm'>
                Analisi persistite lato backend e riapribili in ogni momento.
              </Text>
            </div>
            <Badge variant='light'>{orderedAnalyses.length} analisi</Badge>
          </Group>
        </Card>

        {orderedAnalyses.length === 0 ? (
          <Card withBorder>
            <Text c='dimmed'>
              Nessuna analisi ancora creata. Parti da una selezione di sessioni
              completed.
            </Text>
          </Card>
        ) : (
          <Stack gap='sm'>
            {paginatedAnalyses.map(analysis => (
              <Card key={analysis.analysisId} withBorder>
                <Flex
                  justify='space-between'
                  align={{ base: 'flex-start', md: 'center' }}
                  direction={{ base: 'column', md: 'row' }}
                  gap='sm'
                >
                  <div>
                    <Group gap='xs' mb={4}>
                      <Title order={5}>{analysis.name}</Title>
                      <Badge variant='light'>
                        {analysis.includedCount}/{analysis.selectedCount}{' '}
                        incluse
                      </Badge>
                    </Group>
                    <Text size='sm' c='dimmed'>
                      {analysis.description || 'Nessuna descrizione'}
                    </Text>
                    <Text size='xs' c='dimmed' mt={6}>
                      Creata il {formatDate(analysis.createdAt)}
                    </Text>
                  </div>

                  <Button
                    variant='light'
                    rightSection={<ArrowRight size={16} />}
                    onClick={() =>
                      navigate(
                        `/research/spectral-normalization/legacy/${analysis.analysisId}`
                      )
                    }
                  >
                    Apri analisi
                  </Button>
                </Flex>
              </Card>
            ))}
            {orderedAnalyses.length > analysesPageSize && (
              <Group justify='space-between' align='center'>
                <Text size='xs' c='dimmed'>
                  {orderedAnalyses.length} analisi totali
                </Text>
                <Pagination
                  value={analysisPage}
                  onChange={setAnalysisPage}
                  total={Math.ceil(orderedAnalyses.length / analysesPageSize)}
                  size='sm'
                />
              </Group>
            )}
          </Stack>
        )}
      </Stack>

      <Modal
        opened={opened}
        onClose={handleClose}
        title='Nuova analisi spettrale'
        size='80vw'
      >
        <Stack gap='md'>
          <TextInput
            label='Nome analisi'
            value={name}
            onChange={event => setName(event.currentTarget.value)}
            placeholder='Es. Trial BECOME - confronto baseline vs VR'
          />
          <Textarea
            label='Descrizione'
            value={description}
            onChange={event => setDescription(event.currentTarget.value)}
            placeholder='Descrizione opzionale'
            autosize
            minRows={2}
          />

          <Card withBorder>
            <Stack gap='sm'>
              <Group grow>
                <TextInput
                  label='Cerca per ID'
                  value={searchId}
                  onChange={event => {
                    setSearchId(event.currentTarget.value);
                    setPage(1);
                  }}
                  leftSection={<Search size={16} />}
                />
                <TextInput
                  label='Cerca per utente'
                  value={searchPatient}
                  onChange={event => {
                    setSearchPatient(event.currentTarget.value);
                    setPage(1);
                  }}
                />
                <TextInput
                  label='Cerca per data'
                  value={searchDate}
                  onChange={event => {
                    setSearchDate(event.currentTarget.value);
                    setPage(1);
                  }}
                />
              </Group>

              <Group justify='space-between'>
                <Stack gap={2}>
                  <Text size='sm' c='dimmed'>
                    {selectedIds.length} sessioni selezionate
                  </Text>
                  <Text size='xs' c='dimmed'>
                    Minimo richiesto per l&apos;analisi: 3 sessioni.
                  </Text>
                  <Text size='xs' c='dimmed'>
                    Baseline DB opzionale: se e 0 o null la consideriamo non
                    tracciata, ma la sessione puo comunque essere selezionata.
                  </Text>
                </Stack>
                <Button
                  variant='subtle'
                  size='compact-sm'
                  onClick={() =>
                    setSelectedIds(current =>
                      Array.from(
                        new Set([
                          ...current,
                          ...filteredSessions.map(s => s.id),
                        ])
                      )
                    )
                  }
                >
                  Seleziona pagina corrente
                </Button>
              </Group>

              <ScrollArea h={360}>
                {isLoading ? (
                  <Group justify='center' py='xl'>
                    <Loader size='sm' />
                  </Group>
                ) : (
                  <Stack gap='sm'>
                    {isFetching && (
                      <Group justify='center'>
                        <Loader size='xs' />
                      </Group>
                    )}
                    <Table striped highlightOnHover stickyHeader>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th />
                          <Table.Th>ID</Table.Th>
                          <Table.Th>Utente</Table.Th>
                          <Table.Th>Baseline</Table.Th>
                          <Table.Th>Record biometrici</Table.Th>
                          <Table.Th>Riparata</Table.Th>
                          <Table.Th>Scenario</Table.Th>
                          <Table.Th>Data</Table.Th>
                          <Table.Th>Stato</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {filteredSessions.map(session => {
                          const selected = selectedIds.includes(session.id);
                          const baselineAvailable = hasBaselineValue(
                            session.baseline
                          );

                          return (
                            <Table.Tr key={session.id}>
                              <Table.Td>
                                <Checkbox
                                  checked={selected}
                                  onChange={() => toggleSession(session.id)}
                                />
                              </Table.Td>
                              <Table.Td>{session.id}</Table.Td>
                              <Table.Td>
                                {session.patientCode ?? 'n/a'}
                              </Table.Td>
                              <Table.Td>
                                <Badge
                                  color={baselineAvailable ? 'teal' : 'gray'}
                                  variant='light'
                                >
                                  {baselineAvailable
                                    ? 'Tracciata'
                                    : 'Non tracciata'}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Badge
                                  color={session.hasHrData ? 'teal' : 'red'}
                                  variant='light'
                                >
                                  {session.hasHrData ? 'Presenti' : 'Assenti'}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Badge
                                  color={
                                    session.hasHrDataRepaired ? 'blue' : 'gray'
                                  }
                                  variant='light'
                                >
                                  {session.hasHrDataRepaired ? 'Si' : 'No'}
                                </Badge>
                              </Table.Td>
                              <Table.Td>{session.scenario}</Table.Td>
                              <Table.Td>
                                {formatDate(
                                  session.started_at ??
                                    session.created_at ??
                                    session.updated_at
                                )}
                              </Table.Td>
                              <Table.Td>
                                <Badge variant='light'>{session.status}</Badge>
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                    {configurationsLite &&
                      configurationsLite.totalPages > 1 && (
                        <Group justify='space-between' align='center'>
                          <Text size='xs' c='dimmed'>
                            {configurationsLite.total} sessioni completed
                            trovate
                          </Text>
                          <Pagination
                            value={page}
                            onChange={setPage}
                            total={configurationsLite.totalPages}
                            size='sm'
                          />
                        </Group>
                      )}
                  </Stack>
                )}
              </ScrollArea>
            </Stack>
          </Card>

          <Group justify='flex-end'>
            <Button variant='subtle' onClick={handleClose}>
              Annulla
            </Button>
            <Button
              loading={isCreating}
              onClick={handleCreate}
              disabled={selectedIds.length < 3}
            >
              Calcola e salva
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default SpectralNormalizationPage;
